import { useState, useCallback, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  ActionIcon,
  Text,
  Textarea,
  NumberInput,
  Menu,
  Badge,
  UnstyledButton,
  Blockquote,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconTrash, IconSparkles, IconLanguage, IconChevronDown } from '@tabler/icons-react';
import type { Rephrases } from '@legacy-shared/widgets/core/interpolate';
import type { LanguageOption } from '../../widgets/types';
import { api } from '../../lib/api';
import { getErrorMessage } from '../../lib/error-message';
import { useOrg } from '../../hooks/use-org';

type RephraseEditorProps = {
  message: string | Record<string, string>;
  rephrases: Rephrases | undefined;
  onChange: (rephrases: Rephrases | undefined) => void;
  languages?: LanguageOption[];
  defaultLanguage?: string;
};

function getLangKey(message: string | Record<string, string>, selectedLanguage: string | undefined): string {
  return typeof message === 'string' ? '_default' : (selectedLanguage ?? '_default');
}

function getMessageText(message: string | Record<string, string>, languageCode: string | undefined): string {
  if (typeof message === 'string') return message;
  return (languageCode ? message[languageCode] : undefined) || (Object.values(message)[0] ?? '');
}

function getTotalCount(rephrases: Rephrases | undefined): number {
  if (!rephrases) return 0;
  return Object.values(rephrases).reduce((sum, arr) => sum + arr.length, 0);
}

export function RephraseEditor({ message, rephrases, onChange, languages, defaultLanguage }: RephraseEditorProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { organizationId } = useOrg();
  const hasLanguages = !!languages && languages.length > 0;
  const [selectedLanguage, setSelectedLanguage] = useState<string>(defaultLanguage ?? languages?.[0]?.code ?? '');
  const [generateCount, setGenerateCount] = useState(5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const langKey = getLangKey(message, selectedLanguage);
  const currentRephrases = useMemo(() => rephrases?.[langKey] ?? [], [rephrases, langKey]);
  const currentMessageText = getMessageText(message, hasLanguages ? selectedLanguage : undefined);
  const totalCount = getTotalCount(rephrases);

  const updateRephrases = useCallback(
    (newList: string[]) => {
      const updated = { ...rephrases };
      if (newList.length === 0) {
        delete updated[langKey];
      } else {
        updated[langKey] = newList;
      }
      onChange(Object.keys(updated).length === 0 ? undefined : updated);
    },
    [rephrases, langKey, onChange],
  );

  const handleGenerate = useCallback(async () => {
    if (!currentMessageText.trim() || !organizationId) return;
    setIsGenerating(true);
    setGenerateError(null);

    const { data, error } = await api.ai.rephrase.post({
      organizationId,
      text: currentMessageText,
      count: generateCount,
    });

    if (error) setGenerateError(getErrorMessage(error, 'Failed to generate rephrases'));
    else updateRephrases([...currentRephrases, ...data.rephrases]);

    setIsGenerating(false);
  }, [currentMessageText, organizationId, generateCount, currentRephrases, updateRephrases]);

  const handleUpdate = useCallback(
    (index: number, value: string) => {
      const newList = [...currentRephrases];
      newList[index] = value;
      updateRephrases(newList);
    },
    [currentRephrases, updateRephrases],
  );

  const handleDelete = useCallback(
    (index: number) => {
      updateRephrases(currentRephrases.filter((_, i) => i !== index));
    },
    [currentRephrases, updateRephrases],
  );

  const handleAdd = useCallback(() => {
    updateRephrases([...currentRephrases, '']);
  }, [currentRephrases, updateRephrases]);

  const selectedLangObj = languages?.find((l) => l.code === selectedLanguage);

  return (
    <>
      <UnstyledButton onClick={open}>
        <Text size="xs" c="indigo">
          {totalCount > 0 ? `Rephrases (${totalCount})` : '+ Rephrases'}
        </Text>
      </UnstyledButton>

      <Modal opened={opened} onClose={close} title="Rephrases" size="lg">
        <Stack gap="md">
          <Blockquote color="gray" p="sm" styles={{ icon: { display: 'none' } }}>
            <Text size="sm" c="dimmed">
              {currentMessageText || '(no message)'}
            </Text>
          </Blockquote>

          {hasLanguages && (
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <UnstyledButton>
                  <Group gap={4} align="center" wrap="nowrap">
                    <IconLanguage size={14} style={{ color: 'var(--mantine-color-grape-6)' }} />
                    <Text size="sm" fw={500}>
                      {selectedLangObj?.label ?? selectedLanguage}
                    </Text>
                    <IconChevronDown size={12} style={{ color: 'var(--mantine-color-dimmed)' }} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                {languages?.map((lang) => (
                  <Menu.Item
                    key={lang.code}
                    onClick={() => setSelectedLanguage(lang.code)}
                    fw={lang.code === selectedLanguage ? 600 : undefined}
                    rightSection={
                      lang.code === defaultLanguage ? (
                        <Badge size="xs" variant="light">
                          default
                        </Badge>
                      ) : rephrases?.[lang.code]?.length ? (
                        <Badge size="xs" variant="dot" color="green" />
                      ) : null
                    }
                  >
                    {lang.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}

          <Group gap="xs" align="flex-end">
            <NumberInput
              label="Count"
              value={generateCount}
              onChange={(v) => setGenerateCount(typeof v === 'number' ? v : 5)}
              min={1}
              max={10}
              w={80}
              size="xs"
            />
            <Button
              leftSection={<IconSparkles size={14} />}
              onClick={handleGenerate}
              loading={isGenerating}
              disabled={!currentMessageText.trim()}
              size="xs"
            >
              Generate with AI
            </Button>
          </Group>

          {generateError && (
            <Text size="xs" c="red">
              {generateError}
            </Text>
          )}

          {currentRephrases.map((rephrase, index) => (
            <Group key={index} gap="xs" align="flex-start" wrap="nowrap">
              <Textarea
                flex={1}
                value={rephrase}
                onChange={(e) => handleUpdate(index, e.target.value)}
                minRows={2}
                autosize
                size="xs"
              />
              <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(index)} mt={4}>
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          ))}

          <Button variant="light" leftSection={<IconPlus size={14} />} onClick={handleAdd} size="xs">
            Add rephrase
          </Button>
        </Stack>
      </Modal>
    </>
  );
}
